import React from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { formatDateFromNow } from 'utils/datetime';
import { launchButtonsSx } from 'modules/campaign-creation/styles';
import { getIsCSVCampaign } from 'modules/campaign-creation/utils/getIsCSVCampaign';
import { InitiateLaunchConfirmModal } from 'modules/campaign-creation/components/InitiateLaunchConfirmModal';
import { LaunchCountdownModal } from 'modules/campaign-creation/components/LaunchCountdownModal';
import { useCampaignLaunchFlow } from 'modules/campaign-creation/hooks/useCampaignLaunchFlow';
import { useToast, ToastType } from 'services/toast';
import { ValidationResult } from 'modules/campaign-creation/types/ValidationResult';

export type { ValidationResult };

type Props = {
  isLoading?: boolean;
  /**
   * Validation function that returns automation tasks.
   * The component handles showing error toasts if validation fails.
   */
  validateAndGetTasks?: () => ValidationResult | Promise<ValidationResult>;
};

export const CampaignLaunchButtons: React.FC<Props> = ({
  isLoading = false,
  validateAndGetTasks
}) => {
  const { showToast } = useToast();
  const campaignLastUpdatedAt = useCampaignCreationStore(
    (store) => store.campaignLastUpdatedAt
  );
  const state = useCampaignCreationStore((store) => store.state);
  const isCSVCampaign = getIsCSVCampaign(state);

  const {
    activeModal,
    openInitiateLaunch,
    closeModal,
    setAutomationTasks,
    onSaveAsDraft,
    onInitiateLaunchConfirmCsv,
    onInitiateLaunchConfirmNonCsv,
    onCountdownComplete,
    onCountdownAbort
  } = useCampaignLaunchFlow();

  // Validates tasks and stores them if valid; returns true on success
  const validateAndSetTasks = async (): Promise<boolean> => {
    if (!validateAndGetTasks) {
      return true;
    }
    try {
      const result = await validateAndGetTasks();
      if (!result.isValid) {
        showToast({
          message: result.error || 'Validation failed.',
          type: ToastType.Error
        });
        return false;
      }
      setAutomationTasks(result.tasks);
      return true;
    } catch (error) {
      showToast({
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred during validation.',
        type: ToastType.Error
      });
      return false;
    }
  };

  const handleLaunchClick = async () => {
    if (await validateAndSetTasks()) {
      openInitiateLaunch();
    }
  };

  const handleSaveAsDraftClick = async () => {
    if (await validateAndSetTasks()) {
      onSaveAsDraft();
    }
  };

  // Determine button text and confirm handler based on campaign type
  const buttonText = isCSVCampaign
    ? 'Launch campaign'
    : 'Confirm and launch campaign';
  const onConfirm = isCSVCampaign
    ? onInitiateLaunchConfirmCsv
    : onInitiateLaunchConfirmNonCsv;

  return (
    <>
      <Box sx={launchButtonsSx.container}>
        <Button
          variant="primary"
          sx={launchButtonsSx.launchButton}
          onClick={handleLaunchClick}
          disabled={isLoading}
        >
          {buttonText}
        </Button>
        <Button
          variant="secondary"
          sx={launchButtonsSx.draftButton}
          onClick={handleSaveAsDraftClick}
          disabled={isLoading}
        >
          Save as draft
        </Button>
        {!isCSVCampaign && campaignLastUpdatedAt && (
          <Typography sx={launchButtonsSx.savedText}>
            Draft last saved{' '}
            {formatDateFromNow(campaignLastUpdatedAt.toISOString())}
          </Typography>
        )}
      </Box>

      <InitiateLaunchConfirmModal
        isOpen={activeModal === 'initiateLaunch'}
        onClose={closeModal}
        onConfirm={onConfirm}
        isLoading={isLoading}
      />

      <LaunchCountdownModal
        isOpen={activeModal === 'countdown'}
        onCountdownComplete={onCountdownComplete}
        onAbort={onCountdownAbort}
      />
    </>
  );
};
